import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

// import { getNextPublishStatus } from "utils/publish";

import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { PUBLISH_STATUSES } from "constants/models";
import { MSURL } from "utils/ms";
import { TagDictionaryItem } from "./dictionary/TagDictionary";
import { getNextPublishStatusWithoutScheduled } from "utils/publish";

const pathTip = "/tip/api/v1";
export const TipDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    body: types.maybeNull(types.string),
    videoUrl: types.maybeNull(types.string),
    imageUrl: types.maybeNull(types.string),
    author: types.maybeNull(types.string),
    origin: types.maybeNull(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    tags: types.array(TagDictionaryItem),
  })
  .views(self => ({
    get formData() {
      const uniqueTagsMap = new Map();
      self.tags.map(item => uniqueTagsMap.set(item.id, item));
      const tagArray = Array.from(uniqueTagsMap.values());

      return {
        ...self,
        file: self.imageUrl,
        tags: tagArray.map(({ id, title }) => ({
          id,
          name: title,
        })),
      };
    },
  }));

const ContentTipStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathTip}/tip`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: TipDetailsModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(TipDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isTipLoading: types.optional(types.boolean, false),
    isTipManaging: types.optional(types.boolean, false),
    isTipPublishing: types.optional(types.boolean, false),
    isTipCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateTip(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreTips: flow(function*() {
      self.isTipLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${pathTip}/tip`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipLoading = false;
      }
    }),

    fetchTipDetails: flow(function*(id?: number) {
      self.isTipLoading = true;

      try {
        const details = yield api.get(
          `${pathTip}/tip/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipLoading = false;
      }
    }),

    editTipInfo: flow(function*(payload: any) {
      self.isTipManaging = true;

      try {
        return yield api.put(
          `${pathTip}/tip/${payload.id}`,
          payload.data,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipManaging = false;
      }
    }),

    editTipIcon: flow(function*(payload: any) {
      self.isTipManaging = true;

      try {
        return yield api.putFormData(
          `${pathTip}/tip/${payload.id}/upload-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },

    createTip: flow(function*(payload: any) {
      self.isTipManaging = true;

      try {
        const { id } = yield api.post(
          `${pathTip}/tip`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipManaging = false;
      }
    }),

    changeTipStatus: flow(function*(id: number, status: string) {
      self.isTipPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathTip}/tip/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateTip(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isTipPublishing = false;
      }
    }),
  }));

export default ContentTipStore.create();
